import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getPublicNPS, respondPublicNPS } from '../../services/api';
import { applyTenantBranding, clearTenantBranding } from '../../context/AuthContext';

export default function PublicNPS() {
  const { token } = useParams();
  const [survey,     setSurvey]     = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [selected,   setSelected]   = useState(null);
  const [comment,    setComment]    = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done,       setDone]       = useState(false);

  useEffect(() => {
    getPublicNPS(token)
      .then(r => {
        setSurvey(r.data.survey);
        if (r.data.survey.primary_color) {
          applyTenantBranding(r.data.survey.primary_color);
        }
        if (r.data.survey.score !== null) setDone(true);
      })
      .catch(e => setError(e.response?.data?.error || 'Pesquisa não encontrada'))
      .finally(() => setLoading(false));
    return () => clearTenantBranding();
  }, [token]);

  const onSubmit = async e => {
    e.preventDefault();
    if (selected === null) return toast.error('Selecione uma nota');
    setSubmitting(true);
    try {
      await respondPublicNPS(token, { score: selected, comment });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao enviar');
    } finally { setSubmitting(false); }
  };

  const labelFor = score => {
    if (score <= 6) return 'Insatisfeito';
    if (score <= 8) return 'Satisfeito';
    return 'Muito satisfeito!';
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: 'var(--primary)', color: '#fff', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
        {survey?.logo_url
          ? <img src={survey.logo_url} alt="logo" style={{ height: 40, objectFit: 'contain', borderRadius: 6, background: 'rgba(255,255,255,.15)', padding: 3 }} />
          : <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700 }}>
              {survey?.tenant_name?.[0] || '?'}
            </div>
        }
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{survey?.tenant_name || 'Carregando…'}</div>
          <div style={{ fontSize: 12, opacity: .85 }}>Pesquisa de satisfação</div>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 24 }}>
        <div style={{ background: 'var(--surface)', borderRadius: 16, padding: 32, maxWidth: 480, width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,.08)' }}>
          {loading && <div style={{ textAlign: 'center', padding: 32 }}><div className="spinner" /></div>}

          {!loading && error && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
              <h2 style={{ color: 'var(--gray-900)', marginBottom: 8 }}>Link inválido</h2>
              <p style={{ color: 'var(--gray-500)', fontSize: 14 }}>{error}</p>
            </div>
          )}

          {!loading && done && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>🙏</div>
              <h2 style={{ color: 'var(--gray-900)', marginBottom: 8 }}>Obrigado!</h2>
              <p style={{ color: 'var(--gray-500)', fontSize: 14 }}>Seu feedback foi registrado com sucesso. Ele nos ajuda a melhorar ainda mais.</p>
            </div>
          )}

          {!loading && !error && !done && survey && (
            <form onSubmit={onSubmit}>
              <h2 style={{ margin: '0 0 8px', color: 'var(--gray-900)', fontSize: 20 }}>Como foi seu atendimento?</h2>
              {survey.service_name && (
                <p style={{ color: 'var(--gray-500)', fontSize: 13, marginBottom: 24 }}>
                  Serviço: <strong>{survey.service_name}</strong>
                </p>
              )}

              <p style={{ color: 'var(--gray-700)', fontSize: 14, marginBottom: 16 }}>
                De 0 a 10, qual a probabilidade de você nos recomendar a um amigo?
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(11, 1fr)', gap: 4, marginBottom: 8 }}>
                {Array.from({ length: 11 }, (_, i) => (
                  <button
                    key={i} type="button"
                    onClick={() => setSelected(i)}
                    style={{
                      padding: '10px 0', borderRadius: 8, border: '2px solid',
                      borderColor: selected === i ? 'var(--primary)' : 'var(--gray-200)',
                      background: selected === i ? 'var(--primary)' : 'var(--surface)',
                      color: selected === i ? '#fff' : 'var(--gray-700)',
                      fontWeight: 700, fontSize: 14, cursor: 'pointer',
                    }}
                  >{i}</button>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--gray-400)', marginBottom: 24 }}>
                <span>Nem um pouco</span>
                <span>Com certeza!</span>
              </div>

              {selected !== null && (
                <div style={{ marginBottom: 20, textAlign: 'center', fontSize: 15, fontWeight: 600, color: selected >= 9 ? '#10b981' : selected >= 7 ? '#f59e0b' : '#ef4444' }}>
                  {labelFor(selected)}
                </div>
              )}

              <div className="form-group">
                <label className="label">Deixe um comentário (opcional)</label>
                <textarea className="input" rows={3} value={comment} onChange={e => setComment(e.target.value)} placeholder="O que podemos melhorar?" />
              </div>

              <button type="submit" className={`btn btn-primary${submitting ? ' btn-loading' : ''}`} style={{ width: '100%' }} disabled={submitting || selected === null}>
                {submitting ? '' : 'Enviar avaliação'}
              </button>
            </form>
          )}
        </div>
      </div>

      {!survey?.whitelabel && (
        <div style={{ textAlign: 'center', padding: '16px', color: 'var(--gray-400)', fontSize: 12 }}>
          Powered by <strong style={{ color: 'var(--gray-500)' }}>AtendoApp</strong>
        </div>
      )}
    </div>
  );
}
